import React, { useState } from 'react'
import styled from 'styled-components'

import Button from '../../atoms/Button'
import ButtonText, { ECUrlType } from './ButtonText'
import Image from '../../atoms/Image'
import OuterLink from '../../atoms/OuterLink'
import Overlay from '../../atoms/Overlay'

/**
 * ボタンの並び順
 */
const DISPLAY_POSITIONS: { [key in ECUrlType]: number } = {
  amazon: 1,
  rakuten: 2,
  yahoo: 3,
  wowma: 4,
  toysrus: 5,
  akachan: 6,
  official: 7,
  landing: 8
}

const Container = styled.div`
  margin: 16px auto 0;
  width: 280px;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 25px 0 0;
    width: 100%;
  }
`

const DesktopListContainer = styled.div`
  @media (max-width: ${({ theme }) => theme.mediaQuery.mobile.maxWidth}) {
    display: none;
  }
`

const OpenButton = styled(Button)`
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    display: none;
  }
`

const ButtonIcon = styled(Image)`
  margin: 0 0 0 6px;
  object-fit: contain;
  width: 14px;
  height: 14px;
`

const List = styled.ul`
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  list-style: none;
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    align-items: start;
    display: grid;
    grid-gap: 15px;
    grid-auto-rows: 44px;
    grid-template-columns: repeat(3, 270px);
    justify-content: start;
  }
`

const ListItem = styled.li`
  margin: 10px 0 0 0;
  width: 270px;
  &:first-child {
    margin: 0;
  }
  @media (min-width: ${({ theme }) => theme.mediaQuery.desktop.minWidth}) {
    margin: 0;
  }
`

const ListItemButton = styled(Button)`
  padding: 0 16px;
  height: 44px;
`

const Link = styled(OuterLink)`
  text-decoration: none;
`

const CloseButton = styled.button`
  color: ${({ theme }) => theme.palette.font.light};
  display: block;
  font-size: 12px;
  font-weight: bold;
  margin: 44px auto 0;
  width: 36px;
`

function handleClickECLink(url: string) {
  window.ga('send', {
    hitType: 'event',
    eventCategory: 'EC Link',
    eventAction: 'click',
    eventLabel: 'Award 2019 Autumn',
    eventValue: url
  })
}

type Props = {
  officialUrl: string
  ecUrls: { type: ECUrlType; url: string }[]
}

export default function ECLinkList({ officialUrl, ecUrls }: Props) {
  const [open, setOpen] = useState(false)

  if (!officialUrl && !ecUrls.length) return null

  const official: { type: ECUrlType; url: string }[] = officialUrl ? [{ type: 'landing', url: officialUrl }] : []
  const hasOfficialOnly = official.length === 1 && ecUrls.length === 0
  const sortedECUrls = [...(ecUrls.length ? ecUrls : []), ...official].sort(
    (a, b) => DISPLAY_POSITIONS[a.type] - DISPLAY_POSITIONS[b.type]
  )

  return (
    <Container>
      {/* Desktop */}
      <DesktopListContainer>
        <List>
          {sortedECUrls.map(({ type, url }, index) => (
            <ListItem key={index}>
              <Link onClick={() => handleClickECLink(url)} to={url}>
                <ListItemButton color="white">
                  <ButtonText type={type} />
                </ListItemButton>
              </Link>
            </ListItem>
          ))}
        </List>
      </DesktopListContainer>

      {/* Mobile */}
      {hasOfficialOnly ? (
        <Link onClick={() => handleClickECLink(officialUrl)} to={officialUrl}>
          <OpenButton color="white">
            公式サイトでみる
            <ButtonIcon alt="公式サイトでみる" filename="icon-launch-pink.png" />
          </OpenButton>
        </Link>
      ) : (
        <OpenButton color="white" onClick={() => setOpen(true)}>
          ショッピングサイトでみる
          <ButtonIcon alt="ショッピングサイトでみる" filename="icon-launch-pink.png" />
        </OpenButton>
      )}
      <Overlay open={open}>
        <List>
          {sortedECUrls.map(({ type, url }, index) => (
            <ListItem key={index}>
              <Link onClick={() => handleClickECLink(url)} to={url}>
                <ListItemButton color="white">
                  <ButtonText type={type} />
                </ListItemButton>
              </Link>
            </ListItem>
          ))}
          <ListItem>
            <CloseButton onClick={() => setOpen(false)}>
              <Image filename="icon-close.png" />
              閉じる
            </CloseButton>
          </ListItem>
        </List>
      </Overlay>
    </Container>
  )
}
